<template>
  <sp-menu
    ref="menu"
    class="sp-date-field"
    :open="menuOpen"
    :disabled="disabled"
    :close-on-content-click="closeOnContentClick"
    @focusout="handleFocusOut"
    @update-open="handleMenuOpen"
  >
    <div slot="activator" @keydown="handleActivatorKeydown">
      <slot name="activator">
        <sp-text-field
          ref="field"
          :class="fieldClasses"
          :label="label"
          :placeholder="placeholder"
          :hint="hint"
          :persistent-hint="persistentHint"
          :messages="messages"
          :custom-validity="customValidity"
          :aria-label="ariaLabel"
          :density="density"
          :variant="variant"
          :required="required"
          :readonly="!typable"
          :disabled="disabled"
          :clearable="clearable"
          :persistent-clear="persistentClear"
          :input-test-id="inputTestId"
          :value.prop="displayText"
          prepend-icon="event-color"
          append-icon="chevron-down"
          propagate-append-click
          @input="captureTypedText"
          @click-clear="clearSelection"
        ></sp-text-field>
      </slot>
    </div>

    <sp-card>
      <sp-card-content>
        <slot name="menu-header"></slot>
        <sp-date-picker
          inline
          :day-test-id="dayTestId"
          :prev-month-test-id="prevMonthTestId"
          :next-month-test-id="nextMonthTestId"
          :enable-time="enableTime"
          :range="range"
          :display-format="displayFormat"
          :value-format="valueFormat"
          :disabled="disabled"
          :value.attr="model"
          @change="handleCalendarChange"
        ></sp-date-picker>
      </sp-card-content>
    </sp-card>
  </sp-menu>
</template>

<script setup>
import flatpickr from "flatpickr";
import { computed, ref, toRef, useTemplateRef, watch } from "vue";
import { formAssociatedHost } from "#ce/framework/utils";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { useTextModel } from "#ce/composables/useTextModel";
import { t } from "#ce/shared/forms-i18n";
import { isPresent, toBoolean } from "#ce/utils/props";
import { props as dateFieldProps } from "./props.js";

// sp-date-field submits a value under its name, so a form has to see it.
defineOptions(formAssociatedHost({ shadowRootOptions: { delegatesFocus: true } }));

const emit = defineEmits(["input", "change", "update-open"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(dateFieldProps);

const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const field = useTemplateRef("field");
const menu = useTemplateRef("menu");
const typedText = ref(null);

// flatpickr parses and formats against its own token vocabulary, so the field reuses it rather
// than carrying a second date grammar.
const valueFormat = computed(() => props.valueFormat || (toBoolean(props.enableTime) ? "Y-m-d H:i" : "Y-m-d"));

// A field that can carry a time has to show it, and has to parse it back when the date is typed.
const displayFormat = computed(() => {
  if (props.displayFormat) {
    return props.displayFormat;
  }

  return toBoolean(props.enableTime) ? "m/d/Y h:i K" : "m/d/Y";
});

// flatpickr builds the token patterns its parser needs — the AM/PM marker among them — the first
// time it is instantiated, and the field parses its seeded value before the calendar it wraps has
// mounted.
let localeReady = false;

function ensureParserReady() {
  if (localeReady) {
    return;
  }

  flatpickr(document.createElement("input"), {}).destroy();
  localeReady = true;
}

function parse(text, format) {
  if (!text) {
    return null;
  }

  ensureParserReady();

  const date = flatpickr.parseDate(text, format);

  return Number.isNaN(date?.getTime?.()) ? null : (date ?? null);
}

// RANGE_SEPARATOR joins both ends of a range into the one value the field holds. Every read and
// write of the value works end by end and rejoins.
const RANGE_SEPARATOR = flatpickr.l10ns.default.rangeSeparator;

// Returns `render` applied to each end of `value`, rejoined. Returns "" when any end fails to
// render, so the field never holds half a range.
function mapEnds(value, render) {
  const rendered = String(value)
    .split(RANGE_SEPARATOR)
    .map((end) => render(end.trim()));

  return rendered.some((end) => !end) ? "" : rendered.join(RANGE_SEPARATOR);
}

// Every value goes through the parser, so what is stored is what is rendered — including a local
// time the clock skips, which the parser moves to the hour it resolves to.
function canonicalize(value) {
  if (!value) {
    return "";
  }

  // Text the parser rejects is not a date, so the field holds none: it renders blank, fails
  // `required`, and submits nothing.
  return mapEnds(value, (end) => {
    const date = parse(end, valueFormat.value);

    return date ? flatpickr.formatDate(date, valueFormat.value) : "";
  });
}

// The `value` attribute names the default and the `value` property is the live selection. The
// model keeps the forms-model dirty flag, so an attribute written after the field has a selection
// moves the reset target without overwriting what is selected.
const {
  text: model,
  defaultValue,
  setText,
  setUserText,
  reset,
  setDefaultValue,
} = useTextModel({ normalizeText: canonicalize, normalizeDefault: canonicalize });

// The value the field holds is written in the format that was current when it was set, so a change
// of format has to re-encode it. The default moves with it, and the selection only follows while
// the field is clean — which is what `setDefaultValue` decides.
watch(valueFormat, (format, previous) => {
  if (!previous || previous === format) {
    return;
  }

  const recode = (value) =>
    mapEnds(value, (end) => {
      const date = parse(end, previous);

      return date ? flatpickr.formatDate(date, format) : end;
    }) || value;

  const selection = recode(model.value);

  setDefaultValue(recode(defaultValue.value));

  if (model.value !== selection) {
    setText(selection);
  }
});

const menuOpen = ref(toBoolean(props.open));

function adoptOpenAttribute(open) {
  menuOpen.value = toBoolean(open);
}

watch(() => props.open, adoptOpenAttribute);
const isRange = computed(() => toBoolean(props.range));
const typable = computed(() => toBoolean(props.allowInput) && !isRange.value);

const fieldClasses = computed(() => {
  const classes = [];

  if (!typable.value) {
    classes.push("--is-pick-only");
  }

  return classes;
});
const clearable = computed(() => toBoolean(props.clearable));

const displayText = computed(() =>
  mapEnds(model.value, (end) => {
    const date = parse(end, valueFormat.value);

    return date ? flatpickr.formatDate(date, displayFormat.value) : "";
  }),
);

const { mirrorFormValue, onFormReset, setValidity } = useForm();
const formValue = computed(() => model.value ?? "");

mirrorFormValue(formValue);
onFormReset(() => {
  typedText.value = null;
  reset();
});

const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("date_value_missing"));

// The inner sp-text-field reports to its own ElementInternals, which live inside this shadow root
// and so are invisible to the outer form. Both failures are mirrored onto this host instead, and a
// caller-supplied message outranks the missing-value one.
watch(
  [() => props.customValidity, isRequired, formValue, field],
  ([customValidity, required, value, anchor]) => {
    if (customValidity) {
      setValidity({ customError: true }, customValidity, anchor ?? undefined);
    } else if (required && value === "") {
      setValidity({ valueMissing: true }, validationMessage.value, anchor ?? undefined);
    } else {
      setValidity({});
    }
  },
  { immediate: true, flush: "sync" },
);

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => model.value,
    set: setText,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
  displayText: {
    configurable: true,
    get: () => displayText.value,
  },
});

// Enter and Space open the field for a keyboard user. A typable field keeps those keys for its
// input; Escape closes either kind.
function handleActivatorKeydown(event) {
  if (event.key === "Escape") {
    menuOpen.value = false;
    return;
  }

  if (typable.value || (event.key !== "Enter" && event.key !== " ")) {
    return;
  }

  // The clear control sits inside the field, so its own Enter and Space reach this handler too.
  if (event.composedPath().some((node) => node.classList?.contains("sp-text-field-clear"))) {
    return;
  }

  event.preventDefault();
  menuOpen.value = !menuOpen.value;
}

function handleMenuOpen(event) {
  menuOpen.value = Boolean(event.detail?.[0]);
  emit("update-open", menuOpen.value);
}

// With time enabled the footer is part of the same choice, so the popup stays open through the day
// selection and the user closes it themselves. A range reports its change only once both ends are
// picked, so either mode closes on the change that completes the value.
function handleCalendarChange(event) {
  const [value] = event.detail;

  typedText.value = null;
  commit(value);

  if (!toBoolean(props.enableTime)) {
    menuOpen.value = false;
  }
}

// sp-text-field emits `input` per keystroke, so the text is held until focus leaves the field and
// the user has finished typing.
function captureTypedText(event) {
  if (typable.value) {
    typedText.value = event.detail[0];
  }
}

// Focus moving to the clear button or into the calendar is not the user finishing with the field,
// and committing there would emit the typed value before the control they reached for acts. The
// listener sits on the menu, the outermost element here, so it also hears focus leave the calendar,
// whose month controls are tab stops. The activator only sees focus leave the input: the calendar is
// slotted into the menu's content, so a `focusout` from inside it never reaches the activator.
function handleFocusOut(event) {
  const movedTo = event.relatedTarget;

  if (movedTo && menu.value?.contains(movedTo)) {
    return;
  }

  commitTypedText();
}

// A display format carries a date only if two different days render differently through it. A
// time-only format does not, and flatpickr would fill the missing day with the first of the year,
// so the typed time is moved onto the day already selected.
function displayFormatCarriesDate() {
  const format = displayFormat.value;

  return (
    flatpickr.formatDate(new Date(2026, 0, 1, 12), format) !== flatpickr.formatDate(new Date(2026, 5, 15, 12), format)
  );
}

function withSelectedDay(date) {
  const selected = parse(model.value, valueFormat.value);

  if (!selected || displayFormatCarriesDate()) {
    return date;
  }

  const merged = new Date(selected);
  merged.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);

  return merged;
}

// Text that does not parse leaves the selection alone, and the field falls back to showing it.
function commitTypedText() {
  if (typedText.value === null) {
    return;
  }

  const date = parse(typedText.value, displayFormat.value);

  if (!typedText.value) {
    commit("");
  } else if (date) {
    commit(flatpickr.formatDate(withSelectedDay(date), valueFormat.value));
  }

  typedText.value = null;
  restoreFieldText();
}

// Rejected text stays in the input until it is overwritten, because `displayText` did not change
// and Vue patches the binding only on a change.
function restoreFieldText() {
  if (field.value) {
    field.value.value = displayText.value;
  }
}

function clearSelection() {
  typedText.value = null;
  commit("");
}

function commit(value) {
  if (value === model.value) {
    return;
  }

  setUserText(value);
  emitBubbling("input", model.value);
  emitBubbling("change", model.value);
}
</script>

<style lang="scss">
:host {
  display: block;
}

:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}

// A field the user can only pick into highlights its text on click, which reads as editable.
// user-select inherits, so this reaches the input inside sp-text-field's shadow root.
sp-text-field.--is-pick-only {
  user-select: none;
}

// The calendar renders edge to edge; a popup hanging off a field needs room around it. The padding
// sits on the card so anything slotted above the calendar shares the same inset.
sp-card-content {
  padding: var(--sp-comp-date-field-popup-padding, var(--sp-ref-scale-20));
}

sp-date-picker {
  display: block;
}
</style>
